/* eslint-disable react/jsx-indent */
/* eslint-disable indent */
import checkSvg from '@src/main/assets/icons/check-red.svg';
import { Address } from '@src/main/modules/messages/domain/address/address.interface';
import { SCGroup, Subject } from '@src/main/modules/messages/domain/subject/subject.interface';
import { createRequestFromSubject } from '@src/main/modules/messages/infrastructure/dto/createSubjectRequest';
import { updateRequestFromSubject } from '@src/main/modules/messages/infrastructure/dto/updateSubjectRequest';
import { useAddresses } from '@src/main/modules/messages/ui/addresses/hooks/useAddresses';
import { useCreateSubject } from '@src/main/modules/messages/ui/subjects/hooks/useCreateSubject';
import { useDeleteSubject } from '@src/main/modules/messages/ui/subjects/hooks/useDeleteSubject';
import { useUpdateSubject } from '@src/main/modules/messages/ui/subjects/hooks/useUpdateSubject';
import SubjectForm from '@src/main/modules/messages/ui/subjects/SubjectForm';
import { getSubjectSchema } from '@src/main/modules/messages/ui/subjects/subjectSchema';
import { useSubjects } from '@src/main/modules/messages/ui/subjects/useSubjects';
import { createExportHandlers } from '@src/main/modules/messages/utils/createExportHandlers';
import { normalizeSubject } from '@src/main/modules/messages/utils/formatters';
import { PERMISSIONS } from '@src/main/shared/constants/permissions';
import { useAuth } from '@src/main/shared/context/AuthContext';
import { useToast } from '@src/main/shared/context/ToastContext';
import ContentCard from '@src/main/shared/ui/components/card/ContentCard';
import AppDialog from '@src/main/shared/ui/components/dialog/AppDialog';
import ConfirmDeleteDialog from '@src/main/shared/ui/components/dialog/ConfirmDeleteDialog';
import HeaderTitle from '@src/main/shared/ui/components/header/HeaderTitle';
import AppLoading from '@src/main/shared/ui/components/spinner/AppLoading';
import AppTable from '@src/main/shared/ui/components/table/AppTable';
import TableHeader from '@src/main/shared/ui/components/table/TableHeader';
import { formatList } from '@src/main/shared/utils/formatList';
import { showErrorMessage, showSuccessMessage } from '@src/main/shared/utils/toastUtils';
import { useQueryClient } from '@tanstack/react-query';
import { CheckboxChangeEvent } from 'primereact/checkbox';
import { FC, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

interface TableRow extends Subject {
  id: string | number;
  addressesEmail?: string;
  visibleOnAppDisplay: ReactElement | null;
  scGroups?: SCGroup[];
}

const Subjects: FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { hasPermission } = useAuth();

  const {
    data: subjectsTable = [],
    isLoading: loadingSubjects,
    repository: subjectRepository,
  } = useSubjects();
  const { data: listAddresses = [], isLoading: loadingAddresses } = useAddresses();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newEditSubject, setNewEditSubject] = useState<Subject>({
    subjectId: undefined,
    description: '',
    visibleOnApp: false,
    addresses: [],
    scGroups: [],
  });
  const hasEditPermissions = hasPermission(PERMISSIONS.MESSAGES.SUBJECTS.edit);
  const [selectedSubjects, setSelectedSubjects] = useState<TableRow[]>([]);
  const [subjectToDelete, setSubjectToDelete] = useState<TableRow | null>(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isRefreshing, setIsRefreshing] = useState(false);

  const toolbar = [false, hasEditPermissions, selectedSubjects.length > 0, true, false, false];

  const { mutateAsync: createSubject } = useCreateSubject();
  const { mutateAsync: updateSubject } = useUpdateSubject();
  const { mutateAsync: deleteSubject } = useDeleteSubject();

  const FILE_NAME = t('messages.subjects.fileName');
  const { onExportExcel, onExportPdf } = createExportHandlers(
    subjectsTable,
    normalizeSubject,
    FILE_NAME,
    t,
  );

  const subjectsWithId: TableRow[] = subjectsTable.map((s) => {
    const limit = 3;
    const emails = listAddresses
      .sort((a, b) => a.email.localeCompare(b.email))
      .filter((a) => s.addresses?.some((addr) => addr.addressId === a.addressId))
      .map((a) => a.email)
      .slice(0, limit);

    const addressesEmail =
      emails.join(', ') + (s.addresses.length > limit ? ` (+${s.addresses.length - limit})` : '');

    const scGroupsDisplay =
      s.scGroups && s.scGroups.length > 0
        ? s.scGroups.map((g) => (
            <div key={g.scGroup}>
              {g.scGroup} (Prioridad: {g.priority})
            </div>
          ))
        : '-';

    return {
      id: s.subjectId!,
      ...s,
      addressesEmail,
      visibleOnAppDisplay: s.visibleOnApp ? (
        <img src={checkSvg} alt="Visible" width={16} height={16} />
      ) : null,
      scGroupsDisplay,
    };
  });

  const handleShowCreateSubject = () => {
    setNewEditSubject({
      subjectId: undefined,
      description: '',
      visibleOnApp: false,
      addresses: [],
      scGroups: [],
    });
    setIsModalVisible(true);
  };

  const handleEditSubject = (subject: TableRow) => {
    setNewEditSubject({ ...subject, scGroups: subject.scGroups ?? [] });
    setIsModalVisible(true);
  };

  const handleCreateEdit = async () => {
    if (!subjectRepository) return;
    const schema = getSubjectSchema(t);

    try {
      await schema.parseAsync(newEditSubject);

      if (newEditSubject.subjectId) {
        const updateRequest = updateRequestFromSubject(newEditSubject);
        await updateSubject(updateRequest);
        showSuccessMessage(toast, t('messages.subjects.updatedSuccess'));
      } else {
        const createRequest = createRequestFromSubject(newEditSubject);
        await createSubject(createRequest);
        showSuccessMessage(toast, t('messages.subjects.createdSuccess'));
      }

      await queryClient.invalidateQueries({ queryKey: ['subject'] });
      setIsModalVisible(false);
      setValidationErrors({});
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path.length > 0) errors[err.path[0]] = err.message;
        });
        setValidationErrors(errors);
      } else {
        showErrorMessage(toast, error);
      }
    }
  };

  const handleDeleteSubject = (subject: TableRow) => setSubjectToDelete(subject);
  const confirmDelete = async () => {
    if (!subjectRepository) return;

    try {
      if (isBulkDelete) {
        await Promise.all(selectedSubjects.map((s) => deleteSubject(s.subjectId!)));
        setSelectedSubjects([]);
      } else if (subjectToDelete) {
        await deleteSubject(subjectToDelete.subjectId!);
        setSelectedSubjects((prev) =>
          prev.filter((s) => s.subjectId !== subjectToDelete.subjectId),
        );
      }

      await queryClient.invalidateQueries({ queryKey: ['subject'] });
    } catch (error) {
      showErrorMessage(toast, error);
    } finally {
      setSubjectToDelete(null);
      setIsBulkDelete(false);
      showSuccessMessage(toast, t('messages.addresses.deletedSuccess'));
    }
  };
  const rejectDelete = () => {
    setSubjectToDelete(null);
    setIsBulkDelete(false);
  };

  const handleSelectedChange = (rows: TableRow[]) => setSelectedSubjects(rows);
  const deleteSelectedSubjects = () => selectedSubjects.length && setIsBulkDelete(true);

  const handleAddressChange = (a: Address, e: CheckboxChangeEvent) => {
    const updated = e.checked
      ? [...newEditSubject.addresses, a]
      : newEditSubject.addresses.filter((addr) => addr.addressId !== a.addressId);
    setNewEditSubject({ ...newEditSubject, addresses: updated });
  };

  const handleScGroupChange = (scGroup: string, checked: boolean) => {
    const updatedGroups = checked
      ? [...(newEditSubject.scGroups ?? []), { scGroup, priority: 1 }]
      : (newEditSubject.scGroups ?? []).filter((sg) => sg.scGroup !== scGroup);
    setNewEditSubject({ ...newEditSubject, scGroups: updatedGroups });
  };

  const handleScGroupPriorityChange = (scGroup: string, priority: number) => {
    const updatedGroups = (newEditSubject.scGroups ?? []).map((sg) =>
      sg.scGroup === scGroup ? { ...sg, priority } : sg,
    );
    setNewEditSubject({ ...newEditSubject, scGroups: updatedGroups });
  };

  return (
    <ContentCard>
      <HeaderTitle title={t('messages.subjects.title')} />
      <TableHeader
        onCreate={handleShowCreateSubject}
        onDelete={deleteSelectedSubjects}
        onRefresh={async () => {
          setIsRefreshing(true);
          await queryClient.invalidateQueries({ queryKey: ['subject'] });
          setIsRefreshing(false);
        }}
        onExportExcel={onExportExcel}
        onExportPdf={onExportPdf}
        visibility={toolbar}
        exportable
      />

      <AppLoading isLoading={loadingSubjects || isRefreshing}>
        <AppTable
          headers={[
            { name: t('messages.subjects.description'), field: 'description' },
            { name: t('messages.subjects.visibleOnApp'), field: 'visibleOnAppDisplay' },
            { name: t('Flotas'), field: 'scGroupsDisplay' },
            { name: t('messages.subjects.addresses'), field: 'addressesEmail' },
          ]}
          data={subjectsWithId}
          onEdit={hasEditPermissions ? handleEditSubject : undefined}
          onDelete={hasEditPermissions ? handleDeleteSubject : undefined}
          onSelectedChange={hasEditPermissions ? handleSelectedChange : undefined}
        />

        <AppDialog
          visible={isModalVisible}
          title={
            newEditSubject.subjectId
              ? t('messages.subjects.updateSubject', { subject: newEditSubject.description })
              : t('messages.subjects.createSubject')
          }
          onClose={() => setIsModalVisible(false)}
          disableButtons={loadingAddresses}
          onConfirm={handleCreateEdit}
        >
          <AppLoading isLoading={loadingAddresses}>
            <SubjectForm
              values={newEditSubject}
              availableAddresses={listAddresses}
              availableScGroups={['SC NB', 'SC WB', 'SC LEVEL']}
              handleChange={(field, value) =>
                setNewEditSubject({ ...newEditSubject, [field]: value })
              }
              handleAddressChange={handleAddressChange}
              handleScGroupChange={handleScGroupChange}
              handleScGroupPriorityChange={handleScGroupPriorityChange}
              errors={validationErrors}
            />
          </AppLoading>
        </AppDialog>

        <ConfirmDeleteDialog
          confirmDelete={confirmDelete}
          rejectDelete={rejectDelete}
          visible={isBulkDelete || subjectToDelete !== null}
          message={
            isBulkDelete
              ? t('messages.subjects.confirmBulkDelete', {
                  subjects: formatList(
                    selectedSubjects.map((s) => s.description),
                    t('common.and'),
                  ),
                })
              : t('messages.subjects.confirmDelete', { subject: subjectToDelete?.description })
          }
        />
      </AppLoading>
    </ContentCard>
  );
};

export default Subjects;
